import React, { Suspense, lazy } from "react";
import "./App.css";
import { Box } from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import {
  authEvents,
  isAuthenticated as isUserAuthenticated,
  logoutUser,
} from "./utils/auth";

// Lazy-load components
const Home = lazy(() => import("./components/Home"));
const ResumeForm = lazy(() => import("./components/ResumeForm"));
const ResumePreview = lazy(() => import("./components/ResumePreview"));
const Template2Preview = lazy(() => import("./components/Template2Preview"));
const Template3Preview = lazy(() => import("./components/Template3Preview"));
const Template4Preview = lazy(() => import("./components/Template4Preview"));
const Template5Preview = lazy(() => import("./components/Template5Preview"));
const Template6Preview = lazy(() => import("./components/Template6Preview"));
const Template7Preview = lazy(() => import("./components/Template7Preview"));
const Template8Preview = lazy(() => import("./components/Template8Preview"));
const Template9Preview = lazy(() => import("./components/Template9Preview"));
const Template10Preview = lazy(() => import("./components/Template10Preview"));
const JobDetails = lazy(() => import("./components/JobDetails"));
const Jobs = lazy(() => import("./components/Jobs"));
const JobManager = lazy(() => import("./components/JobManager"));
const LoginPage = lazy(() => import("./components/LoginPage"));
const RegisterPage = lazy(() => import("./components/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./components/ForgotPasswordPage"));
const AboutPage = lazy(() => import("./components/AboutPage"));
const TemplatesPage = lazy(() => import("./components/TemplatesPage"));
const PdfEditorPage = lazy(() => import("./components/PdfEditorPage"));
const ImageEditorPage = lazy(() => import("./components/ImageEditorPage"));

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;

function InactivityLogoutManager() {
  const navigate = useNavigate();
  const timerRef = React.useRef(null);
  const isLoggingOutRef = React.useRef(false);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const doAutoLogout = React.useCallback(async () => {
    if (isLoggingOutRef.current || !isUserAuthenticated()) return;

    isLoggingOutRef.current = true;
    clearTimer();
    try {
      await logoutUser();
    } finally {
      navigate("/login", { replace: true });
      isLoggingOutRef.current = false;
    }
  }, [clearTimer, navigate]);

  const resetTimer = React.useCallback(() => {
    if (!isUserAuthenticated() || isLoggingOutRef.current) {
      clearTimer();
      return;
    }

    clearTimer();
    timerRef.current = window.setTimeout(doAutoLogout, INACTIVITY_TIMEOUT_MS);
  }, [clearTimer, doAutoLogout]);

  React.useEffect(() => {
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];

    events.forEach((eventName) => {
      window.addEventListener(eventName, resetTimer, { passive: true });
    });
    window.addEventListener(authEvents.AUTH_CHANGED_EVENT, resetTimer);
    window.addEventListener("storage", resetTimer);

    resetTimer();

    return () => {
      clearTimer();
      events.forEach((eventName) => {
        window.removeEventListener(eventName, resetTimer);
      });
      window.removeEventListener(authEvents.AUTH_CHANGED_EVENT, resetTimer);
      window.removeEventListener("storage", resetTimer);
    };
  }, [clearTimer, resetTimer]);

  return null;
}

function AppRoutes() {
  const location = useLocation();
  const isHomeRoute = location.pathname === "/";

  return (
    <Box className={isHomeRoute ? "app-shell" : "app-shell abstract-bg"}>
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/job-search" element={<Jobs />} />
          <Route path="/jobs/:jobId" element={<JobDetails />} />
          <Route path="/resume-builder" element={<ResumeForm />} />
          <Route path="/resume-preview/1" element={<Template4Preview />} />
          <Route path="/resume-preview/2" element={<Template6Preview />} />
          <Route path="/resume-preview/3" element={<Template7Preview />} />
          <Route path="/resume-preview/4" element={<Template8Preview />} />
          <Route path="/resume-preview/5" element={<Template5Preview />} />
          <Route path="/resume-preview/6" element={<Template9Preview />} />
          <Route path="/resume-preview/7" element={<Template3Preview />} />
          <Route path="/resume-preview/8" element={<Template2Preview />} />
          <Route path="/resume-preview/9" element={<ResumePreview />} />
          <Route path="/resume-preview/10" element={<Template10Preview />} />
          <Route path="/admin/job" element={<JobManager />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/logout" element={<Navigate to="/" replace />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/pdf-editor" element={<PdfEditorPage />} />
          <Route path="/image-editor" element={<ImageEditorPage />} />

          {/* 🚨 Catch-all for invalid routes test*/}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Box>
  );
}

function App() {
  return (
    <Router>
      <InactivityLogoutManager />
      <Navbar />
      <AppRoutes />
    </Router>
  );
}

export default App;
